// Cascade paths, in words: what deleting one row of the selected entity does, beside the diagram.
// Pointing at an item lights up its path in the diagram; picking one keeps it lit and brings it into
// view. Without a selected entity it says how to start. It also holds the database switch.
import type { DeleteDatabase, DeleteImpact, DeleteStep } from '@modelviewer/core';
import { ChevronDown, ChevronRight, GitFork, OctagonAlert, X, type LucideProps } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '../lib/cn';

interface Props {
  /** What deleting a row of the selected entity does; null while none is selected. */
  impact: DeleteImpact | null;
  database: DeleteDatabase;
  /** The database the model was made for, when it says. */
  guessed: DeleteDatabase | null;
  onDatabase: (database: DeleteDatabase) => void;
  /** Turns cascade paths off. */
  onClose: () => void;
  /** Starts folded to its summary (narrow diagrams). */
  folded: boolean;
  /** Its height limit. */
  className: string;
  /** Steps (indexes into `impact.steps`) whose paths to light up; null for none. */
  onPoint: (steps: number[] | null) => void;
  /** Steps whose paths stay lit after a click (again to let go). */
  pinned: number[] | null;
  onPin: (steps: number[] | null) => void;
  /** An item was picked: the steps it stands for, and the relationship to select when it is about one. */
  onPick: (steps: number[], relationship: number | null) => void;
}

const DATABASES: { id: DeleteDatabase; label: string; rules: string }[] = [
  { id: 'sqlServer', label: 'SQL Server', rules: 'One path of cascading actions to each table and no cycles; RESTRICT is NO ACTION.' },
  { id: 'postgres', label: 'PostgreSQL', rules: 'Any number of cascade paths; NO ACTION is checked when the statement ends.' },
];

/** The cascade paths icon: one card fanning out to the ones it reaches. */
export function CascadeIcon({ className, ...props }: LucideProps) {
  return <GitFork {...props} className={cn('-scale-y-100', className)} />;
}

export function DeletePanel({ impact, database, guessed, onDatabase, onClose, folded: startFolded, className, onPoint, pinned, onPin, onPick }: Props) {
  const [folded, setFolded] = useState(startFolded);
  const steps = useMemo(() => impact?.steps ?? [], [impact]);
  const conflicts = impact?.conflicts ?? [];
  const start = impact?.start ?? '';

  const groups = useMemo(() => {
    // Rows deleted: one item per table, reached along one or more steps (the first is the shortest).
    const deleted = new Map<string, number[]>();
    const setNull: number[] = [];
    const block: number[] = [];
    steps.forEach((s, i) => {
      if (s.effect === 'delete') deleted.set(s.deletes!, [...(deleted.get(s.deletes!) ?? []), i]);
      else if (s.effect === 'setNull') setNull.push(i);
      else block.push(i);
    });
    return { deleted: [...deleted], setNull, block };
  }, [steps]);
  const byRelationship = useMemo(() => new Map(steps.map((s) => [s.relationship, s])), [steps]);
  /** How the delete gets to a step: `Blog → Post → Comment`. */
  const path = (s: DeleteStep) => <span className="block text-[11.5px] break-words text-muted">{[start, ...s.path.map((r) => byRelationship.get(r)!.dependent)].join(' → ')}</span>;

  const counts = [
    groups.deleted.length > 0 && `${groups.deleted.length} ${groups.deleted.length === 1 ? 'table' : 'tables'} deleted`,
    groups.setNull.length > 0 && `${groups.setNull.length} set to null`,
    groups.block.length > 0 && `${groups.block.length} can block`,
    conflicts.length > 0 && `${conflicts.length} refused by SQL Server`,
  ].filter(Boolean);

  /** Props that light up `list` while pointed at, and pin it on a click. */
  const lights = (list: number[], relationship: number | null) => {
    const on = pinned !== null && pinned.length === list.length && pinned.every((p, i) => p === list[i]);
    return {
      onMouseEnter: () => onPoint(list),
      onMouseLeave: () => onPoint(null),
      onFocus: () => onPoint(list),
      onBlur: () => onPoint(null),
      onClick: () => {
        onPin(on ? null : list);
        if (!on) onPick(list, relationship);
      },
      'aria-pressed': on,
      className: cn('block w-full rounded-md px-2 py-1.5 text-left hover:bg-hover pointer-coarse:py-2', on && 'bg-active'),
    };
  };

  return (
    <section className={cn('flex w-[340px] max-w-full flex-col rounded-lg border border-border bg-panel-2/95 text-[12.5px] shadow-sm backdrop-blur', className)} aria-label="Cascade paths" data-testid="delete-panel">
      <div className="flex flex-none items-start gap-0.5 p-1">
        {impact ? (
          <button type="button" onClick={() => setFolded(!folded)} aria-expanded={!folded} className="flex min-w-0 flex-1 items-start gap-1.5 rounded-md px-1.5 py-1 text-left hover:bg-hover">
            {folded ? <ChevronRight size={14} className="mt-0.5 flex-none text-faint" /> : <ChevronDown size={14} className="mt-0.5 flex-none text-faint" />}
            <span className="min-w-0">
              <span className="block font-semibold text-text">Deleting one {start}</span>
              <span className="block text-muted">{counts.length ? counts.join(' · ') : 'changes nothing else'}</span>
            </span>
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-start gap-1.5 px-1.5 py-1">
            <CascadeIcon size={14} className="mt-0.5 flex-none text-faint" aria-hidden />
            <span className="min-w-0">
              <span className="block font-semibold text-text">Cascade paths</span>
              <span className="block text-muted">Select a table to see what deleting one of its rows does.</span>
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Turn cascade paths off"
          aria-label="Turn cascade paths off"
          className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-faint hover:bg-hover hover:text-text pointer-coarse:h-9 pointer-coarse:w-9"
        >
          <X size={14} />
        </button>
      </div>
      {!folded && (
        <div className="flex flex-none items-center gap-2 px-2.5 pb-2">
          <span className="text-[11.5px] text-faint" aria-hidden>
            As
          </span>
          <div className="flex rounded-md bg-panel p-0.5" role="radiogroup" aria-label="Database">
            {DATABASES.map((d) => (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={database === d.id}
                title={`${guessed === d.id ? `The model is for ${d.label}. ` : ''}${d.rules}`}
                onClick={() => onDatabase(d.id)}
                className={cn('h-6 rounded px-2 text-[12px] pointer-coarse:h-8 pointer-coarse:px-2.5', database === d.id ? 'bg-active text-text' : 'text-muted hover:text-text')}
              >
                {d.label}
              </button>
            ))}
          </div>
          <span className="text-[11.5px] text-faint" aria-hidden>
            does it
          </span>
        </div>
      )}
      {impact && !folded && (
        <div className="nowheel min-h-0 overflow-y-auto border-t border-border px-1 pt-1 pb-2">
          {steps.length === 0 && <p className="px-2 py-1.5 text-muted">No other table points at {start}: only its own row is deleted.</p>}

          {conflicts.length > 0 && (
            <Group title="SQL Server refuses these foreign keys" effect="conflict">
              <p className="px-2 pb-1 text-[11.5px] text-faint">It allows one path of cascading actions (cascade or set null) to each table, and no cycles. Make one of them no action or restrict.</p>
              {conflicts.map((c) => (
                <button type="button" key={c.entity} {...lights(c.steps, steps[c.steps[0]!]!.relationship)}>
                  <span className="font-medium text-text">{c.entity}</span>
                  <span className="text-muted"> {c.cycle ? 'is reached again around a cycle' : `is reached along ${c.steps.length} paths`}</span>
                  {c.steps.map((i) => (
                    <span key={i}>{path(steps[i]!)}</span>
                  ))}
                </button>
              ))}
            </Group>
          )}

          {groups.deleted.length > 0 && (
            <Group title="Rows deleted with it" effect="delete">
              {groups.deleted.map(([table, list]) => {
                const first = steps[list[0]!]!;
                return (
                  <button type="button" key={table} {...lights(list, null)}>
                    <span className="font-medium text-text">{table}</span>
                    {first.onlyIf && <span className="text-muted"> when it is a {first.onlyIf}</span>}
                    {path(first)}
                    {list.length > 1 && <span className="block text-[11.5px] text-faint">and along {list.length - 1} more {list.length === 2 ? 'path' : 'paths'}</span>}
                  </button>
                );
              })}
            </Group>
          )}

          {groups.setNull.length > 0 && (
            <Group title="Foreign key set to null" effect="setNull">
              {groups.setNull.map((i) => {
                const s = steps[i]!;
                return (
                  <button type="button" key={i} {...lights([i], s.relationship)}>
                    <Column step={s} />
                    {s.onlyIf && <span className="text-muted"> when it is a {s.onlyIf}</span>}
                    {path(s)}
                  </button>
                );
              })}
            </Group>
          )}

          {groups.block.length > 0 && (
            <Group title="Stops the delete when it has rows" effect="block">
              {groups.block.map((i) => {
                const s = steps[i]!;
                return (
                  <button type="button" key={i} {...lights([i], s.relationship)}>
                    <Column step={s} />
                    <span className="ml-1.5 rounded bg-panel px-1 font-mono text-[10.5px] text-muted">{s.action}</span>
                    {s.onlyIf && <span className="text-muted"> when it is a {s.onlyIf}</span>}
                    {path(s)}
                    {s.alsoDeleted && <Note>Its rows are deleted along another path too; only rows left over stop the delete.</Note>}
                    {s.client === 'setNull' && <Note>EF Core sets it to null on rows it has loaded, before deleting.</Note>}
                    {s.client === 'delete' && <Note>EF Core deletes the rows it has loaded, before deleting.</Note>}
                  </button>
                );
              })}
            </Group>
          )}

          <p className="px-2 pt-2 text-[11px] leading-snug text-faint">
            From each relationship&apos;s delete behavior. EF Core&apos;s client-side behaviors only change rows it has loaded.
          </p>
        </div>
      )}
    </section>
  );
}

const SWATCH = {
  delete: 'bg-effect-delete',
  setNull: 'bg-effect-setNull',
  block: 'bg-effect-block',
  conflict: 'bg-effect-delete',
} as const;

function Group({ title, effect, children }: { title: string; effect: keyof typeof SWATCH; children: React.ReactNode }) {
  return (
    <div className="pt-1.5">
      <h3 className="flex items-center gap-1.5 px-2 pb-0.5 text-[11px] font-semibold tracking-wider text-faint uppercase">
        {effect === 'conflict' ? <OctagonAlert size={12} className="text-effect-delete" aria-hidden /> : <span className={cn('h-2 w-2 rounded-full', SWATCH[effect])} aria-hidden />}
        {title}
      </h3>
      {children}
    </div>
  );
}

/** The foreign key a step changes or is stopped by: `Post.BlogId`. */
function Column({ step }: { step: DeleteStep }) {
  return (
    <span className="font-medium text-text">
      {step.dependent}.<span className="font-mono text-[11.5px]">{step.foreignKey.join(', ')}</span>
    </span>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <span className="block text-[11.5px] leading-snug text-faint">{children}</span>;
}
